import {
  Box,
  CloseButton,
  Flex,
  Group,
  Button as MantineButton,
  Modal,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { ConfirmModal } from 'src/design-system/ConfirmModal';
import { Button } from 'src/design-system/Button';
import { moneyOrNoFigure } from '../../notRecorded';
import { AllocationConfig, NO_PEOPLE, SHARE_KEY } from './allocations';
import { CashflowField, CashflowFields, missingRequired } from './CashflowFields';
import { PeopleAllocations } from './PeopleAllocations';

// Every form is this wide, so the fields and the people table sit in the same place in each; an
// income's extra after-tax column is what sets it. Mantine converts a number-like `size` with
// rem() and turns anything else into `var(--modal-size-<value>)`, so a CSS expression there
// resolves to nothing and the modal silently falls back to 440px. The cap lives on `content`
// instead, which also has to override its default `flex: 0 0 var(--modal-size)` before it will
// shrink at all.
const CARD_WIDTH = 760;
const GUTTER = 32;

/**
 * One total in the footer, with the parts of it worth reading beside it, such as this client's
 * share. A value of null is an amount not entered yet.
 */
export type SummaryFigure = {
  label: string;
  value: number | null;
  parts?: { label: string; value: number | null }[];
};

/**
 * Offered only when an item already exists, and it removes the item outright rather than the
 * adviser's view of it: a shared item goes for everyone who holds it. `message` is what the
 * warning says, which is where the members losing it are named.
 */
export type DeletionConfig = {
  onDelete: () => Promise<void>;
  title: string;
  message: string;
  /** Names what is going, e.g. "Delete expenditure". */
  confirmLabel: string;
};

type CashflowModalShellProps = {
  opened: boolean;
  onClose: () => void;
  title: string;
  submitLabel: string;
  onSubmit: () => void;
  isSaving?: boolean;
  /** Only the figures the form does not already show. */
  summary: SummaryFigure[];
  /** Shown in place of the summary when the amounts cannot be converted. */
  summaryNote?: string | null;
  testId: string;
  fields: CashflowField[][];
  allocation: AllocationConfig;
  /** Sits under the title, e.g. saying who else an item is shared with. */
  notice?: string;
  /** Called on any change to the item's own fields, e.g. to clear a refusal they may answer. */
  onFieldChange?: () => void;
  /** Absent while adding, since there is nothing yet to delete. */
  deletion?: DeletionConfig;
};

/**
 * One column in the order the task runs: describe the item, say who it applies to and how much
 * each person's share is, then check the total beside the button that saves it. The header and
 * footer stay put and only the form between them scrolls, so the card is as tall as its content
 * and no taller.
 */
export function CashflowModalShell({
  opened,
  onClose,
  title,
  submitLabel,
  onSubmit,
  isSaving = false,
  summary,
  summaryNote,
  testId,
  fields,
  allocation,
  notice,
  onFieldChange,
  deletion,
}: CashflowModalShellProps) {
  const [confirmOpened, confirm] = useDisclosure(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<{ message: string } | null>(null);

  // Save is disabled by exactly these reasons, so the tooltip on the disabled button can never be
  // left without one. They are listed on hover because the messages under the fields only show
  // once the adviser has started, and a person's figure can be the problem further down the form.
  // `allocation.error` includes the backend's refusal, which clears once anything in the form
  // changes: sending the same item again would only be refused again.
  const blockers = [
    ...missingRequired(fields),
    ...(allocation.allocations.length === 0 ? [NO_PEOPLE] : []),
    ...(allocation.error ? [allocation.error] : []),
    ...personProblems(allocation),
  ];
  const canSubmit = blockers.length === 0;

  const handleDelete = async () => {
    if (!deletion || isDeleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deletion.onDelete();
      confirm.close();
    } catch {
      // The caller reports the failure its own way; the dialog stays open so the adviser can
      // see the item is still there and try again rather than assuming it went.
      setDeleteError({ message: 'Could not delete this entry. Try again.' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      size={CARD_WIDTH}
      variant="multi"
      styles={{
        content: {
          backgroundColor: 'transparent',
          // Nothing inside holds the card open, so an `auto` basis would shrink it to its
          // narrowest field.
          flex: `0 1 ${CARD_WIDTH}px`,
          maxWidth: 'calc(100vw - 48px)',
        },
        // The body's padding would sit inside the content's height cap alongside the card.
        body: { padding: 0 },
      }}
    >
      <Flex
        direction="column"
        w="100%"
        // Mantine caps the content at the viewport less its vertical offset on both sides and
        // scrolls it there, which would carry the footer off the bottom. Capping the card at the
        // same height keeps the scroll inside the form, between the header and the footer.
        mah="calc(100dvh - 2 * var(--modal-y-offset, 5dvh))"
        bg="white"
        style={{ borderRadius: 32, overflow: 'hidden' }}
        data-testid={testId}
      >
        <Box px={GUTTER} pt={28} pb={8}>
          <Group justify="space-between" align="center" wrap="nowrap" gap="md">
            <Text c="black" fw={FONT_WEIGHTS.semibold} size="displaySm">
              {title}
            </Text>
            <CloseButton
              onClick={onClose}
              radius="xl"
              size="md"
              bg="gray.10"
              c="white"
              aria-label="Close"
            />
          </Group>
          {notice && (
            <Text c="gray.7" fw={FONT_WEIGHTS.medium} fz={14} lh="20px" mt={8}>
              {notice}
            </Text>
          )}
        </Box>

        <Stack
          gap="md"
          px={GUTTER}
          pt={12}
          pb={24}
          style={{ overflowY: 'auto', minHeight: 0, flex: '1 1 auto' }}
        >
          {/* Only an existing item can be deleted, so `deletion` is what marks one */}
          <CashflowFields rows={fields} revealRequired={!!deletion} onAnyChange={onFieldChange} />
          <PeopleAllocations {...allocation} />
        </Stack>

        <Group
          justify="space-between"
          align="flex-end"
          wrap="nowrap"
          gap="lg"
          px={GUTTER}
          pt="md"
          pb="lg"
          style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Summary figures={summary} note={summaryNote} />
          <Group gap="lg" wrap="nowrap" style={{ flexShrink: 0 }}>
            {deletion && (
              <MantineButton
                variant="subtle"
                color="red"
                radius="xl"
                onClick={confirm.open}
                disabled={isSaving}
                data-testid="delete-cashflow"
              >
                Delete
              </MantineButton>
            )}
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <Tooltip
              label={
                <Stack gap={2}>
                  {/* Two people of the same name can share a problem, so position is the key */}
                  {blockers.map((reason, index) => (
                    <Text key={index} inherit>
                      {reason}
                    </Text>
                  ))}
                </Stack>
              }
              disabled={canSubmit}
              multiline
              maw={320}
              events={{ hover: true, focus: true, touch: true }}
            >
              {/* A disabled button fires no mouse events, so the tooltip hangs on a wrapper */}
              <Box data-testid="submit-cashflow-wrapper" tabIndex={canSubmit ? undefined : 0}>
                <Button
                  radius="xl"
                  onClick={onSubmit}
                  disabled={!canSubmit}
                  loading={isSaving}
                  data-testid="submit-cashflow"
                >
                  {submitLabel}
                </Button>
              </Box>
            </Tooltip>
          </Group>
        </Group>
      </Flex>

      {deletion && (
        <ConfirmModal
          opened={confirmOpened}
          onClose={confirm.close}
          onConfirm={handleDelete}
          title={deletion.title}
          description={deletion.message}
          confirmLabel={deletion.confirmLabel}
          error={deleteError}
          isLoading={isDeleting}
        />
      )}
    </Modal>
  );
}

/**
 * Every total reads the same way: its label, the figure, then its parts in small type, side by
 * side when there are two (an income's before and after tax). A form whose figures are all on
 * screen already passes none, and the footer holds only its buttons.
 */
function Summary({ figures, note }: { figures: SummaryFigure[]; note?: string | null }) {
  const currency = useCurrency();

  if (note) {
    return (
      <Text c="gray.6" fw={FONT_WEIGHTS.medium} fz={14} lh="20px">
        {note}
      </Text>
    );
  }

  return (
    <Group gap="xl" align="flex-start" wrap="nowrap" miw={0}>
      {figures.map(figure => (
        <Stack key={figure.label} gap={0} miw={0}>
          <Text c="gray.7" fw={FONT_WEIGHTS.semibold} fz={12} lh="18px">
            {figure.label}
          </Text>
          <Text c="gray.10" fw={FONT_WEIGHTS.bold} fz={20} lh="28px">
            {moneyOrNoFigure(figure.value, currency)}
          </Text>
          {figure.parts?.map(part => (
            <Text key={part.label} c="gray.7" fw={FONT_WEIGHTS.medium} fz={12} lh="18px">
              {part.label}: {moneyOrNoFigure(part.value, currency)}
            </Text>
          ))}
        </Stack>
      ))}
    </Group>
  );
}

/** Each person's figure that stops a save, named by whose row it is on. */
function personProblems({
  allocations,
  extraFields,
  nameOf,
  fieldError,
}: AllocationConfig): string[] {
  const fields = [{ key: SHARE_KEY, label: 'Share' }, ...extraFields];
  return allocations.flatMap(allocation =>
    fields.flatMap(field => {
      const value = allocation.amounts[field.key];
      // A negative money figure is clamped to zero only once the field loses focus, and a share is
      // never clamped, so either can block the save without anything showing in red. An empty
      // figure is allowed: it saves as not recorded.
      const problem = amountProblem(value, field.label) ?? fieldError?.(allocation, field.key);
      return problem ? [`${nameOf(allocation.clientId)}: ${problem}`] : [];
    }),
  );
}

function amountProblem(value: number | '' | undefined, label: string): string | undefined {
  if (typeof value !== 'number') return undefined;
  if (!Number.isFinite(value)) return `${label} is not a valid amount`;
  return value < 0 ? `${label} cannot be negative` : undefined;
}
